<template>
  <SoAlert
    v-if="query?.isError"
    title="Failed to render registrations table"
    data-testid="enrolment-list-error"
  />
  <SoDataTableServer
    v-else
    ref="enrolment-list-table"
    v-model:options="tableOptions"
    data-testid="enrolment-list-table"
    searchable
    :is-show-all-pagination-option="isShowAllPaginationOption"
    :headers="tableHeaders"
    :data="tableData"
    @fetch="fetchData"
  >
    <template #item.enrolmentFailureMessage="{ internalItem }">
      <div class="break-word">
        {{ internalItem.columns.enrolmentFailureMessage }}
      </div>
    </template>
    <template #item.mappedStatus="{ internalItem }">
      <SoTooltip :text="internalItem.columns.mappedStatus.explanation">
        <SoStatus :status="internalItem.columns.mappedStatus.status" />
      </SoTooltip>
    </template>
    <template #item.email="{ internalItem }">
      <div class="d-flex align-center" data-testid="enrolment-table-email-cell">
        {{ internalItem.columns.email }}
        <SoCopyToClipboard :plain-text="internalItem.columns.email">
          <template #button="{ copyToClipboard }">
            <v-btn
              type="sidebar-icon"
              size="x-small"
              variant="plain"
              icon="copy sm"
              @click.stop="copyToClipboard"
            />
          </template>
        </SoCopyToClipboard>
      </div>
    </template>
    <template #item.enrolmentAutomatedCommunication="{ internalItem }">
      <SoButton
        v-if="internalItem.raw.communicationsSent"
        data-testid="open-enrolment-automated-comms-btn"
        type="table-action"
        title="View"
        @click="viewAutomatedCommunicationRecord(internalItem.raw)"
      />
      <p v-else>No</p>
    </template>
    <template #tableActions>
      <SoButton
        title="Statuses Table"
        class="ml-auto"
        large
        @click="showStatusesTable = !showStatusesTable"
      />
      <SoExportCsvButton
        :file-name="`${segment}-registrations`"
        disabled-tooltip="Select your filters to export your results"
        :disabled="!isExportEnabled"
        :data="exportData"
        :labels="tableHeaders"
        disabled-for-no-filters
        @export="exportCsv"
      />
    </template>
    <template #itemActions="{ internalItem }">
      <SoButton
        data-testid="open-enrolment-btn"
        type="table-action"
        title="View"
        @click="viewRecord(internalItem.raw)"
      />
    </template>
    <template v-if="isShowBrokersPieChart" #doughnutChart>
      <v-card
        class="mb-4 elevation-0"
        :title="doughnutChartData.title"
        :loading="brokersQuery?.isFetching"
      >
        <SoDoughnutChart
          class="ma-3"
          :data-set="doughnutChartData.dataSet"
          :labels="doughnutChartData.labels"
          :background-colors="doughnutChartData.backgroundColors"
          style="max-width: 560px; max-height: 180px"
          data-testid="brokers-doughnut-chart"
        />
      </v-card>
    </template>
  </SoDataTableServer>

  <SoDialog
    v-model="showStatusesTable"
    card-title="Statuses Explained"
    :max-width="900"
  >
    <SoSimpleTable
      ref="enrolment-status-modal-table"
      :headers="statusTableHeaders"
      :items="statusTableItems"
    />
  </SoDialog>
  <OpsEnrolmentModal
    v-if="viewedEnrolment"
    ref="enrolment-detail-modal"
    v-model="isEnrolmentDetailsModalOpen"
    :enrolment="viewedEnrolment"
  />
  <OpsEnrolmentAutomatedCommunicationModal
    v-if="viewedEnrolment"
    ref="enrolment-automated-communication-modal"
    v-model="isEnrolmentAutomatedCommunicationModalOpen"
    :enrolment="viewedEnrolment"
  />
</template>

<script lang="ts">
  import { defineComponent, PropType } from "vue"
  import { cloneDeep, isEqual } from "lodash-es"
  import dayjs from "dayjs"
  import { formatDate, safeDayjs } from "@/helpers/dateHelper"
  import SoDataTableServer, {
    SoDataTableData,
    SoDataTableOptions,
  } from "@/components/dataTable/SoDataTableServer.vue"
  import SoExportCsvButton, {
    SoExportCsvButtonData,
  } from "@/components/dataTable/SoExportCsvButton.vue"
  import { FilterType } from "@/types/components/SoDataTable"
  import SoAlert from "@/components/SoAlert.vue"
  import SoButton from "@/components/formControls/SoButton.vue"
  import SoDialog from "@/components/SoDialog.vue"
  import SoStatus from "@/components/SoStatus.vue"
  import SoTooltip from "@/components/SoTooltip.vue"
  import SoSimpleTable from "@/components/SoSimpleTable.vue"
  import SoCopyToClipboard from "@/components/SoCopyToClipboard.vue"
  import SoDoughnutChart from "@/components/charts/SoDoughnutChart.vue"
  import {
    BrokerFilterOptionsQueryResult,
    EnrolmentFilters,
    EnrolmentService,
    EnrolmentsGroupedByBrokerQueryResult,
    EnrolmentQueryResult,
    GetEnrolmentsParams,
  } from "@/services/operations/EnrolmentService"
  import {
    TableFilters,
    tableFilters,
    tableHeaders,
  } from "@/views/operations/enrolments/opsEnrolmentsListConfig"
  import { EnrolmentSegment, EnrolmentSegmentType } from "@/types/Enrolment"
  import {
    EnrolmentStatus,
    EnrolmentStatusDetailsMap,
  } from "@/constants/enrolment"
  import { externalRoutes } from "@/constants/routes/externalRoutes"
  import { QueryResult } from "@/api/utils/types"
  import { useThemeHelper } from "@/theme/themeHelper"
  import OpsEnrolmentModal from "./OpsEnrolmentModal.vue"
  import OpsEnrolmentAutomatedCommunicationModal from "./OpsEnrolmentAutomatedCommunicationModal.vue"
  import { Enrolment } from "@/models/operations/enrolment/enrolment"

  export default defineComponent({
    components: {
      SoDoughnutChart,
      SoAlert,
      SoButton,
      SoStatus,
      SoDialog,
      SoTooltip,
      SoSimpleTable,
      SoDataTableServer,
      SoCopyToClipboard,
      SoExportCsvButton,
      OpsEnrolmentModal,
      OpsEnrolmentAutomatedCommunicationModal,
    },
    props: {
      segment: {
        type: String as PropType<EnrolmentSegmentType>,
        required: true,
      },
    },
    data() {
      return {
        tableOptions: {
          filters: tableFilters,
          orderBy: {
            created_at: "desc",
          },
        } as Partial<SoDataTableOptions>,
        query: null as QueryResult<EnrolmentQueryResult | null> | null,
        exportQuery: null as QueryResult<EnrolmentQueryResult | null> | null,
        brokersQuery:
          null as QueryResult<EnrolmentsGroupedByBrokerQueryResult | null> | null,
        brokerOptionsQuery:
          null as QueryResult<BrokerFilterOptionsQueryResult | null> | null,
        theme: useThemeHelper(),
        mappedTableFilters: {} as GetEnrolmentsParams,
        showStatusesTable: false,
        statusTableHeaders: [
          "Enrolment Status",
          "Is Supply Initiated?",
          "Explanation",
        ],
        externalRoutes,
        viewedEnrolment: null as Enrolment | null,
        isEnrolmentDetailsModalOpen: false,
        isEnrolmentAutomatedCommunicationModalOpen: false,
      }
    },
    computed: {
      tableHeaders() {
        return tableHeaders.filter(
          (header) =>
            !header.segments || header.segments.includes(this.segment),
        )
      },
      tableData(): SoDataTableData {
        return {
          isLoading: !!this.query?.isFetching,
          totalItems: this.query?.data?.totalCount ?? 0,
          items: this.query?.data?.records ?? [],
        }
      },
      exportData(): SoExportCsvButtonData {
        return {
          isLoading: !!this.exportQuery?.isFetching,
          items: this.exportQuery?.data?.records ?? [],
        }
      },
      isShowAllPaginationOption() {
        const filters = this.mappedTableFilters.filters
        if (!filters?.createdDateFrom || !filters?.onboardingStatus) {
          return false
        }
        const from = safeDayjs(filters.createdDateFrom)
        const to = safeDayjs(filters.createdDateTo)
        const differenceInDays = to?.diff(from, "d")
        const onboardingStatus = filters.onboardingStatus
        const isOnboardingStatusValid =
          onboardingStatus.length > 0 &&
          !onboardingStatus.includes(EnrolmentStatus.DONE)
        return isOnboardingStatusValid && differenceInDays === 1
      },
      doughnutChartData() {
        const enrolmentsByBroker: number[] = []
        const brokerNames: string[] = []
        this.brokersQuery?.data?.enrolmentsByBroker?.forEach((brokerGroup) => {
          const broker = brokerGroup.broker ?? "No Broker"
          enrolmentsByBroker.push(brokerGroup.count)
          brokerNames.push(broker)
        })
        const colors = this.theme.colors
        return {
          dataSet: enrolmentsByBroker,
          labels: brokerNames,
          backgroundColors: [
            colors.fire,
            colors.sun,
            colors.earth,
            colors.sky,
            colors.fireLight,
            colors.sunLight,
            colors.earthLight,
            colors.skyLight,
            colors.fireDark,
            colors.sunDark,
            colors.earthDark,
            colors.skyDark,
          ],
          title: `Rejections by Broker: from ${formatDate(
            this.mappedTableFilters.filters.createdDateFrom,
          )} to ${formatDate(this.mappedTableFilters.filters.createdDateTo)}`,
        }
      },
      statusTableItems() {
        return Object.values(EnrolmentStatusDetailsMap).map((status) => [
          status.short,
          status.supply,
          status.long,
        ])
      },
      isExportEnabled() {
        const filters = this.mappedTableFilters.filters
        return (
          !!filters?.createdDateFrom ||
          !!filters?.createdDateTo ||
          !!filters?.broker?.length ||
          !!filters?.onboardingStatus?.length
        )
      },
      isShowBrokersPieChart() {
        return (
          this.shouldFetchBrokersPieChart &&
          this.brokersQuery?.data?.enrolmentsByBroker?.length
        )
      },
      shouldFetchBrokersPieChart() {
        const filters = this.mappedTableFilters.filters
        return (
          !!filters?.createdDateFrom &&
          !!filters?.createdDateTo &&
          this.segment === EnrolmentSegment.JUNIFER_ERRORS
        )
      },
    },
    watch: {
      "brokerOptionsQuery.data": {
        handler(data) {
          const brokerOptions = data?.brokerOptions ?? {}
          const brokerFilter = this.tableOptions.filters?.broker

          if (
            brokerFilter?.type === FilterType.CHECKBOX &&
            !isEqual(brokerFilter.options, brokerOptions)
          ) {
            brokerFilter.options = brokerOptions
          }
        },
        deep: true,
      },
    },
    mounted() {
      this.fetchBrokerOptions()
      if (this.segment === EnrolmentSegment.JUNIFER_ERRORS) {
        const filters = cloneDeep(this.tableOptions.filters) as TableFilters
        const defaultDaysInterval = this.getDefaultDateFilter()
        filters.createdAt.value.to = defaultDaysInterval.to
        filters.createdAt.value.from = defaultDaysInterval.from
        const dataTable = this.$refs["enrolment-list-table"] as InstanceType<
          typeof SoDataTableServer
        >
        dataTable.setOptions({ filters }, true)
      }
    },
    methods: {
      viewRecord(viewedEnrolment) {
        this.viewedEnrolment = viewedEnrolment
        this.isEnrolmentDetailsModalOpen = true
      },
      viewAutomatedCommunicationRecord(viewedEnrolment) {
        this.viewedEnrolment = viewedEnrolment
        this.isEnrolmentAutomatedCommunicationModalOpen = true
      },
      mapFilters(filters: TableFilters): EnrolmentFilters {
        return {
          createdDateFrom: safeDayjs(filters.createdAt.value.from),
          createdDateTo: safeDayjs(filters.createdAt.value.to),
          broker: filters.broker.value ?? undefined,
          onboardingStatus: filters.onboardingStatus.value ?? undefined,
          segment: this.segment,
        }
      },
      fetchBrokerOptions() {
        const params = {
          search: "",
          filters: {
            createdDateFrom: dayjs("2020-01-01"),
            createdDateTo: dayjs().endOf("day"),
            segment: this.segment,
          },
        }
        this.brokerOptionsQuery =
          EnrolmentService.getBrokerFilterOptions(params)
      },
      fetchData(tableOptions: SoDataTableOptions) {
        this.mappedTableFilters = {
          search: tableOptions.search,
          filters: this.mapFilters(tableOptions.filters as TableFilters),
          page: tableOptions.page,
          orderBy: tableOptions.orderBy,
          pageSize: tableOptions.pageSize,
        }
        this.fetchEnrolments()
        this.fetchEnrolmentsGroupedByBroker()
      },
      fetchEnrolments() {
        this.query = EnrolmentService.getEnrolments(this.mappedTableFilters)
      },
      fetchEnrolmentsGroupedByBroker() {
        if (this.shouldFetchBrokersPieChart) {
          this.brokersQuery = EnrolmentService.getEnrolmentsGroupedByBroker(
            this.mappedTableFilters,
          )
        }
      },
      getDefaultDateFilter() {
        const to = dayjs().startOf("day")
        // Set 'from' to 3 days before 'to' if it's Monday, otherwise 1 day before.
        const from =
          to.day() === 1 ? to.subtract(3, "day") : to.subtract(1, "day")
        return {
          to: to.toDate(),
          from: from.toDate(),
        }
      },
      exportCsv() {
        this.exportQuery = EnrolmentService.getEnrolments(
          this.mappedTableFilters,
        )
      },
    },
  })
</script>
